import { computed, Injectable, signal } from "@angular/core";
import { AccessLevel, AccessLevels, AccessLevelValue } from "../../../pojo/Fls";
import { RoleRights } from "./role.rights";
import { RoleRightsConfigService } from "./role.rights.config.service";
import {
    SearchCriteriaItem,
    SearchCriteriaService,
} from "../../../services/search/search-criteria/search.criteria.service";

export type ContactRoleOption = { id: number; name: string };

/**
 * Baseline (system-wide) Connect25 role rights editor state.
 * Org- and contact-scoped editors can follow the same pattern with different loaders/savers.
 */
@Injectable()
export class SeSecurityBaselineConfigStore {
    readonly rights = signal<RoleRights.RightWithRolesConfig[]>([]);
    /** Columns for the matrix view — all organization roles (active), sorted by name */
    readonly matrixRoleColumns = signal<ContactRoleOption[]>([]);
    readonly loading = signal(true);
    readonly saving = signal(false);
    readonly error = signal<string | null>(null);
    readonly selectedRoleId = signal<number | null>(null);

    /** Union of all roles referenced in the loaded config, sorted by name */
    readonly contactRoles = computed((): ContactRoleOption[] => {
        const map = new Map<number, string>();
        for (const r of this.rights()) {
            for (const a of r.roles) {
                map.set(a.roleId, a.roleName);
            }
        }
        return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ id, name }));
    });

    async load(): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const [config, orgRoleItems] = await Promise.all([
                RoleRightsConfigService.getRightsConfig(),
                SearchCriteriaService.getOrganizationRoles(false, true),
            ]);
            this.rights.set(config);

            const matrixCols = mapOrganizationRoleItemsToColumns(orgRoleItems);
            this.matrixRoleColumns.set(matrixCols.length ? matrixCols : collectContactRolesFromRightsList(config));
        } catch (e) {
            console.error("SeSecurityBaselineConfigStore.load", e);
            this.error.set("Failed to load security configuration.");
            this.rights.set([]);
            this.matrixRoleColumns.set([]);
        } finally {
            this.loading.set(false);
        }
    }

    selectRole(roleId: number | null): void {
        this.selectedRoleId.set(roleId);
    }

    levelForSelectedRole(rightName: string): AccessLevel {
        const roleId = this.selectedRoleId();
        if (!roleId) return AccessLevels.None;
        const def = this.rights().find((r) => r.rightName === rightName);
        if (!def) return AccessLevels.None;
        return def.roles.find((r) => r.roleId === roleId)?.accessLevel ?? AccessLevels.None;
    }

    levelForRole(rightName: string, roleId: number): AccessLevel {
        const def = this.rights().find((r) => r.rightName === rightName);
        if (!def) return AccessLevels.None;
        return def.roles.find((r) => r.roleId === roleId)?.accessLevel ?? AccessLevels.None;
    }

    /** System-wide default for this role/right (same as {@link levelForRole} in baseline mode). */
    globalLevelForRole(rightName: string, roleId: number): AccessLevel {
        return this.levelForRole(rightName, roleId);
    }

    async setAccessLevelForSelectedRole(rightName: string, level: AccessLevel): Promise<void> {
        const roleId = this.selectedRoleId();
        if (!roleId) return;
        await this.setAccessLevelForRole(rightName, roleId, level);
    }

    async setAccessLevelForRole(
        rightName: string,
        roleId: number,
        level: AccessLevel,
        opts?: { skipSaving?: boolean; suppressError?: boolean },
    ): Promise<void> {
        const prev = this.rights();
        const def = prev.find((r) => r.rightName === rightName);
        if (!def) return;

        const nextRoles = this.applySingleRoleChangeToRoles(def, def.roles, roleId, level);

        const nextRights = prev.map((r) => (r.rightName === rightName ? { ...r, roles: nextRoles } : r));
        this.rights.set(nextRights);

        const trackSaving = !opts?.skipSaving;
        if (trackSaving) this.saving.set(true);
        if (!opts?.suppressError) this.error.set(null);
        try {
            await RoleRightsConfigService.updateRolesForRight(rightName, [roleId], level);
        } catch (e) {
            console.error("SeSecurityBaselineConfigStore.setAccessLevelForRole", e);
            if (!opts?.suppressError) {
                this.error.set("Failed to save. Reverting.");
            }
            this.rights.set(prev);
        } finally {
            if (trackSaving) this.saving.set(false);
        }
    }

    /**
     * Matrix "Apply to All" row: set the same access level for this right on every visible role column.
     * Skips "Read" when the right does not define read access.
     */
    async applyLevelToVisibleRoleColumnsForRight(
        rightName: string,
        roleIds: number[],
        level: AccessLevel,
    ): Promise<void> {
        if (!roleIds.length) return;
        const prev = this.rights();
        const def = prev.find((r) => r.rightName === rightName);
        if (!def) return;
        if (level === AccessLevels.Read && !def.readDescription) return;

        const uniqueRoleIds = [...new Set(roleIds)];

        let roles = def.roles;
        for (const roleId of uniqueRoleIds) {
            roles = this.applySingleRoleChangeToRoles(def, roles, roleId, level);
        }
        const next = prev.map((r) => (r.rightName === rightName ? { ...r, roles } : r));

        this.saving.set(true);
        this.error.set(null);
        try {
            this.rights.set(next);
            await RoleRightsConfigService.updateRolesForRight(rightName, uniqueRoleIds, level);
        } catch (e) {
            console.error("SeSecurityBaselineConfigStore.applyLevelToVisibleRoleColumnsForRight", e);
            this.error.set("Failed to save. Reverting.");
            this.rights.set(prev);
        } finally {
            this.saving.set(false);
        }
    }

    /**
     * Merge one role's level into a working role list (same semantics as a single matrix cell edit).
     */
    private applySingleRoleChangeToRoles(
        def: RoleRights.RightWithRolesConfig,
        currentRoles: RoleRights.RoleWithAccessLevel[],
        roleId: number,
        level: AccessLevel,
    ): RoleRights.RoleWithAccessLevel[] {
        const roleName =
            currentRoles.find((r) => r.roleId === roleId)?.roleName ??
            def.roles.find((r) => r.roleId === roleId)?.roleName ??
            this.matrixRoleColumns().find((r) => r.id === roleId)?.name ??
            this.contactRoles().find((r) => r.id === roleId)?.name ??
            "Unknown";

        if (level === AccessLevels.None) {
            return currentRoles.filter((r) => r.roleId !== roleId);
        }
        return [...currentRoles.filter((r) => r.roleId !== roleId), { roleId, roleName, accessLevel: level }];
    }
}

export function mapOrganizationRoleItemsToColumns(items: SearchCriteriaItem[]): ContactRoleOption[] {
    return items.map((item) => ({ id: Number(item.itemId), name: String(item.itemName).trim() }));
}

export function collectContactRolesFromRightsList(config: RoleRights.RightWithRolesConfig[]): ContactRoleOption[] {
    const map = new Map<number, string>();
    for (const r of config) {
        for (const a of r.roles) {
            map.set(a.roleId, a.roleName);
        }
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => ({ id, name }));
}

export function accessLevelSelectOptionsForRight(
    def: RoleRights.RightWithRolesConfig,
): { value: string; label: string }[] {
    const opts: { value: string; label: string }[] = [{ value: AccessLevels.None, label: "No access" }];
    if (def.readDescription) {
        opts.push({ value: AccessLevels.Read, label: "Read" });
    }
    opts.push({ value: AccessLevels.Full, label: "Full control" });
    return opts;
}

/** Disables options below the current effective level (org security: no demotion). */
export function accessLevelSelectOptionsNonDemoting(
    def: RoleRights.RightWithRolesConfig,
    effectiveFloor: AccessLevel,
): { value: string; label: string; disabled?: boolean }[] {
    return accessLevelSelectOptionsForRight(def).map((o) => ({
        ...o,
        disabled: AccessLevelValue[o.value as AccessLevel] < AccessLevelValue[effectiveFloor],
    }));
}

export function accessLevelShortLabel(level: AccessLevel): string {
    if (level === AccessLevels.Full) return "Full control";
    if (level === AccessLevels.Edit) return "Edit";
    if (level === AccessLevels.Read) return "Read";
    return "No access";
}
